import { useEffect, useLayoutEffect, useRef } from "react";
import type { Conversation } from "../../../types/conversation";
import type { Message } from "../../../types/message";
import MessageRenderer from "../../message/message-renderer";
import { formatDateTime } from "../../../utils";

interface MessageListProps {
  conversationId?: string;
  messages: Message[];
  previousConversations?: Conversation[];
  hasMorePreviousConversations?: boolean;
  isLoadingPreviousConversations?: boolean;
  onLoadPreviousConversations?: () => void;
}

type RenderMessageOptions = {
  isPrevious?: boolean;
};

function renderMessageBubble(
  message: Message,
  key: string,
  options?: RenderMessageOptions,
) {
  if (message.role === "system") return null;

  const isPrevious = !!options?.isPrevious;
  const isUserMessage = message.role === "user";

  const messageClass = isPrevious
    ? isUserMessage
      ? "rounded-tl-none border border-slate-200 bg-slate-100 text-slate-700"
      : "rounded-tr-none border border-blue-200 bg-blue-100 text-blue-900"
    : isUserMessage
      ? "rounded-tl-none bg-slate-200/70 text-slate-800"
      : "rounded-tr-none bg-blue-600 text-white";

  return (
    <div
      key={key}
      className={`flex ${isUserMessage ? "justify-start" : "justify-end"}`}
    >
      <div
        className={`max-w-[90%] sm:max-w-[60%] md:max-w-[50%] lg:max-w-[40%] rounded-2xl px-4 py-2 text-[15px] leading-relaxed break-words ${
          isPrevious ? "" : "shadow-sm"
        } ${messageClass}`}
      >
        <MessageRenderer message={message} />
      </div>
    </div>
  );
}

export function MessageList({
  conversationId,
  messages,
  previousConversations = [],
  hasMorePreviousConversations = false,
  isLoadingPreviousConversations = false,
  onLoadPreviousConversations,
}: MessageListProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const previousScrollHeightRef = useRef<number | null>(null);
  const shouldStickToBottomRef = useRef(true);

  const visiblePreviousConversations = previousConversations
    .filter((conversation) =>
      conversation.messages.some((message) => message.role !== "system"),
    )
    .slice()
    .reverse();

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    shouldStickToBottomRef.current = true;
    container.scrollTop = container.scrollHeight;
  }, [conversationId, messages]);

  useEffect(() => {
    const container = containerRef.current;
    const content = contentRef.current;
    if (!container || !content || typeof ResizeObserver === "undefined") {
      return;
    }

    const observer = new ResizeObserver(() => {
      if (shouldStickToBottomRef.current) {
        container.scrollTop = container.scrollHeight;
      }
    });

    observer.observe(content);
    return () => observer.disconnect();
  }, []);

  useLayoutEffect(() => {
    const container = containerRef.current;
    const previousScrollHeight = previousScrollHeightRef.current;

    if (container && previousScrollHeight !== null) {
      container.scrollTop += container.scrollHeight - previousScrollHeight;
      previousScrollHeightRef.current = null;
    }
  }, [previousConversations.length]);

  useEffect(() => {
    if (!isLoadingPreviousConversations) {
      previousScrollHeightRef.current = null;
    }
  }, [isLoadingPreviousConversations]);

  const handleScroll = () => {
    const container = containerRef.current;
    if (container) {
      const distanceFromBottom =
        container.scrollHeight - container.scrollTop - container.clientHeight;
      shouldStickToBottomRef.current = distanceFromBottom <= 24;
    }

    if (
      !container ||
      container.scrollTop > 80 ||
      !hasMorePreviousConversations ||
      isLoadingPreviousConversations ||
      !onLoadPreviousConversations
    ) {
      return;
    }

    previousScrollHeightRef.current = container.scrollHeight;
    onLoadPreviousConversations();
  };

  return (
    <div
      ref={containerRef}
      onScroll={handleScroll}
      className="relative flex-1 overflow-y-auto bg-slate-50 px-4 py-3 [overflow-anchor:none]"
    >
      {isLoadingPreviousConversations && (
        <div
          className="pointer-events-none absolute left-0 right-0 top-2 z-10 flex justify-center"
          role="status"
        >
          <span className="h-fit rounded-full bg-white/90 px-3 py-1 text-xs text-slate-500 shadow-sm">
            Carregando histórico...
          </span>
        </div>
      )}

      <div ref={contentRef} className="flex flex-col gap-3">
        {visiblePreviousConversations.map((conversation) => (
          <div key={conversation.id} className="space-y-3">
            <div className="flex items-center justify-center py-1">
              <span className="rounded-full border border-slate-300 bg-white px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-600">
                Atendimento anterior
                {conversation.createdAt
                  ? ` • encerrado em ${formatDateTime(conversation.createdAt)}`
                  : ""}
              </span>
            </div>
            <div className="space-y-3">
              {conversation.messages.map((message, index) =>
                renderMessageBubble(
                  message,
                  `previous-${conversation.id}-${index}`,
                  {
                    isPrevious: true,
                  },
                ),
              )}
            </div>
          </div>
        ))}

        {visiblePreviousConversations.length > 0 && (
          <div className="flex items-center gap-3 py-2">
            <div className="h-px flex-1 bg-slate-300" />
            <span className="rounded-full bg-slate-200 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-700">
              Início do atendimento atual
            </span>
            <div className="h-px flex-1 bg-slate-300" />
          </div>
        )}

        <div className="space-y-3">
          {messages.map((message, index) =>
            renderMessageBubble(message, `current-${index}`),
          )}
        </div>

      </div>
    </div>
  );
}
